import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Text from "../../../shared/ui/Typography/Text";

import { createInvitation } from "../api/invitations";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// CompanyInvitationWorkflow — feature widget. Owns ALL state for the
// bounded three-step invitation creation flow: recipient → details →
// review → POST → success surface. No Redux slice, no useReducer, no
// state machine, no step registry. See
// migration/phase14-workflow-boundary-plan.md.
//
// Step ownership is inline and explicit. There is no STEP_CONFIG /
// STEP_MAP / transition map / step metadata object — each step's
// validators, fields, and render are co-located in their own
// renderXStep() helper inside this file.
//
// Exactly ONE async edge: the final submit() POSTs to the backend.

const EMAIL_MAX = 254;
const MESSAGE_MAX = 1000;

const ROLE_OPTIONS = [
  { value: "member", label: "Рієлтор" },
  { value: "admin", label: "Керівник" },
];

const ROLE_VALUES = ROLE_OPTIONS.map((o) => o.value);

const INITIAL_VALUES = {
  email: "",
  role: "member",
  message: "",
};

const INITIAL_STEP_ERRORS = {
  recipient: {},
  details: {},
  review: {},
};

const EDITABLE_FIELDS = ["email", "role", "message"];

const looksLikeEmail = (value) => /.+@.+\..+/.test(value);

const roleLabelFor = (value) => {
  const match = ROLE_OPTIONS.find((o) => o.value === value);
  return match ? match.label : value;
};

// ============================================================
// VALIDATION
// Feature-local pure functions. No schema engine, no registry.
// Each step owns its own validator. validateAll runs both
// defensively for the pre-submit re-check.
// ============================================================

const validateRecipient = (values) => {
  const errors = {};
  const email = (values.email ?? "").trim();
  if (email.length === 0) {
    errors.email = "Введіть електронну адресу.";
  } else if (email.length > EMAIL_MAX) {
    errors.email = `Не довше ${EMAIL_MAX} символів.`;
  } else if (!looksLikeEmail(email)) {
    errors.email = "Введіть коректну електронну адресу.";
  }
  return errors;
};

const validateDetails = (values) => {
  const errors = {};
  const role = values.role ?? "";
  if (!ROLE_VALUES.includes(role)) {
    errors.role = "Виберіть роль.";
  }
  const message = values.message ?? "";
  if (message.length > MESSAGE_MAX) {
    errors.message = `Не довше ${MESSAGE_MAX} символів.`;
  }
  return errors;
};

const validateAll = (values) => ({
  recipient: validateRecipient(values),
  details: validateDetails(values),
});

// Maps a flat { email?, role?, message?, _form? } map (from
// parseDrfFieldErrors OR from validateAll) into the per-step
// shape used by stepErrors state. Explicit field ownership; no
// registry.
const routeServerErrors = (parsed) => {
  const next = { recipient: {}, details: {}, review: {} };
  if (!parsed) return next;
  if (parsed.email) next.recipient.email = parsed.email;
  if (parsed.role) next.details.role = parsed.role;
  if (parsed.message) next.details.message = parsed.message;
  if (parsed._form) next.review._form = parsed._form;
  return next;
};

// Rewind helper — earliest step that contains a non-empty
// errors map. Explicit ordered list; no dynamic lookup.
const earliestFailingStep = (stepErrors) => {
  if (stepErrors.recipient && Object.keys(stepErrors.recipient).length > 0) {
    return "recipient";
  }
  if (stepErrors.details && Object.keys(stepErrors.details).length > 0) {
    return "details";
  }
  if (stepErrors.review && Object.keys(stepErrors.review).length > 0) {
    return "review";
  }
  return null;
};

// ============================================================
// COMPONENT
// ============================================================

function CompanyInvitationWorkflow({
  companyId,
  companyName,
  onCancelHref,
  canInviteAdmin = false,
  onDirtyChange,
  onBusyChange,
  writesSuspended = false,
}) {
  const roleOptions = useMemo(
    () =>
      canInviteAdmin
        ? [
            { value: "member", label: "Рієлтор" },
            { value: "admin", label: "Керівник" },
          ]
        : [{ value: "member", label: "Рієлтор" }],
    [canInviteAdmin],
  );
  const roleValues = useMemo(
    () => roleOptions.map((o) => o.value),
    [roleOptions],
  );

  // ----- Step-3 / state-3 / 4 / 5 / 8: workflow state -----
  const [currentStep, setCurrentStep] = useState("recipient");
  const [values, setValues] = useState(INITIAL_VALUES);
  const [stepErrors, setStepErrors] = useState(INITIAL_STEP_ERRORS);
  const [saveState, setSaveState] = useState("idle");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [createdToken, setCreatedToken] = useState("");
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  // Accessibility — focus heading after step transition.
  const headingRef = useRef(null);
  const writesSuspendedRef = useRef(writesSuspended);
  writesSuspendedRef.current = Boolean(writesSuspended);

  useEffect(() => {
    setValues(INITIAL_VALUES);
    setStepErrors(INITIAL_STEP_ERRORS);
    setCurrentStep("recipient");
    setSaveState("idle");
    setCreatedToken("");
    setCopyFeedback(null);
    setConfirmed(false);
  }, [companyId, canInviteAdmin]);

  const draftDirty = useMemo(() => {
    if (saveState === "success" || saveState === "saving") return true;
    if (currentStep !== "recipient") return true;
    if (confirmed) return true;
    if ((values.email || "").trim() !== "") return true;
    if ((values.message || "").trim() !== "") return true;
    if ((values.role || "") !== (INITIAL_VALUES.role || "")) return true;
    return false;
  }, [confirmed, currentStep, saveState, values]);

  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(draftDirty);
  }, [draftDirty, onDirtyChange]);

  useEffect(() => {
    if (typeof onBusyChange === "function") {
      onBusyChange(saveState === "saving");
    }
  }, [onBusyChange, saveState]);

  useEffect(() => {
    if (saveState === "success") return;
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, [currentStep, saveState]);

  const saving = saveState === "saving" || writesSuspended;

  const validateDetailsLocal = useCallback((vals) => {
    const errors = {};
    const role = vals.role ?? "";
    if (!roleValues.includes(role)) {
      errors.role = "Виберіть роль.";
    }
    const message = vals.message ?? "";
    if (message.length > MESSAGE_MAX) {
      errors.message = `Не довше ${MESSAGE_MAX} символів.`;
    }
    return errors;
  }, [roleValues]);

  const roleLabelForLocal = useCallback((value) => {
    const match = roleOptions.find((o) => o.value === value);
    return match ? match.label : value;
  }, [roleOptions]);

  // ----- Field handlers — each clears its own field error
  // and any stale review-level form error. Confirmation
  // checkbox is independent of the value triplet.
  const handleEmailChange = useCallback((event) => {
    const next = event.target.value;
    setValues((prev) => ({ ...prev, email: next }));
    setStepErrors((prev) => ({
      ...prev,
      recipient: {},
      review: {},
    }));
  }, []);

  const handleRoleChange = useCallback((event) => {
    const next = event.target.value;
    setValues((prev) => ({ ...prev, role: next }));
    setStepErrors((prev) => {
      const detailsNext = { ...prev.details };
      delete detailsNext.role;
      return { ...prev, details: detailsNext, review: {} };
    });
  }, []);

  const handleMessageChange = useCallback((event) => {
    const next = event.target.value;
    setValues((prev) => ({ ...prev, message: next }));
    setStepErrors((prev) => {
      const detailsNext = { ...prev.details };
      delete detailsNext.message;
      return { ...prev, details: detailsNext, review: {} };
    });
  }, []);

  const handleConfirmedChange = useCallback((event) => {
    setConfirmed(event.target.checked);
    setStepErrors((prev) => ({ ...prev, review: {} }));
  }, []);

  // ============================================================
  // STEP TRANSITIONS — Step 5
  // Four explicit functions. No registry, no map, no engine.
  // ============================================================

  const goNext = useCallback(() => {
    if (saving) return;
    if (currentStep === "recipient") {
      const errs = validateRecipient(values);
      if (Object.keys(errs).length > 0) {
        setStepErrors((prev) => ({ ...prev, recipient: errs }));
        return;
      }
      // Clear ONLY the step being left. Destination-step errors
      // (e.g. server-routed role errors) survive so the user
      // sees them on arrival. Rollback semantics — plan §6.
      setStepErrors((prev) => ({ ...prev, recipient: {} }));
      setCurrentStep("details");
      return;
    }
    if (currentStep === "details") {
      const errs = validateDetailsLocal(values);
      if (Object.keys(errs).length > 0) {
        setStepErrors((prev) => ({ ...prev, details: errs }));
        return;
      }
      setStepErrors((prev) => ({ ...prev, details: {} }));
      setCurrentStep("review");
      return;
    }
    // No "next" from review — submit handles that.
  }, [currentStep, saving, values, validateDetailsLocal]);

  const goPrevious = useCallback(() => {
    if (saving) return;
    if (currentStep === "details") {
      setStepErrors((prev) => ({ ...prev, details: {} }));
      setCurrentStep("recipient");
      return;
    }
    if (currentStep === "review") {
      setStepErrors((prev) => ({ ...prev, review: {} }));
      setCurrentStep("details");
      return;
    }
    // No "previous" from recipient — cancel handles that.
  }, [currentStep, saving]);

  const cancel = useCallback(() => {
    setCurrentStep("recipient");
    setValues(INITIAL_VALUES);
    setStepErrors(INITIAL_STEP_ERRORS);
    setConfirmed(false);
    setSaveState("idle");
    setSubmittedEmail("");
    setCreatedToken("");
    setCopyFeedback(null);
  }, []);

  // ============================================================
  // SUBMIT — Step 6 (the only async edge) + Step 7 (rewind)
  // ============================================================
  const submit = useCallback(async () => {
    if (saving) return;
    if (!confirmed) return;

    // Defense-in-depth: re-run all validators. Browser autofill
    // could have changed inputs between the last per-step check
    // and the submit click.
    const allErrors = {
      recipient: validateRecipient(values),
      details: validateDetailsLocal(values),
    };
    const hasRecipientErr = Object.keys(allErrors.recipient).length > 0;
    const hasDetailsErr = Object.keys(allErrors.details).length > 0;
    if (hasRecipientErr || hasDetailsErr) {
      const next = {
        recipient: allErrors.recipient,
        details: allErrors.details,
        review: {},
      };
      setStepErrors(next);
      const target = earliestFailingStep(next);
      if (target) setCurrentStep(target);
      return;
    }

    setStepErrors(INITIAL_STEP_ERRORS);
    setSaveState("saving");

    const body = {
      email: values.email.trim(),
      role: values.role,
    };
    const trimmedMessage = values.message.trim();
    if (trimmedMessage.length > 0) {
      body.message = trimmedMessage;
    }

    try {
      const created = await createInvitation(companyId, body);
      setSubmittedEmail(body.email);
      setCreatedToken(created?.token || "");
      setCopyFeedback(null);
      setSaveState("success");
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_FIELDS);
      const routed = routeServerErrors(parsed);
      // If parseDrfFieldErrors had no recognizable shape, ensure
      // we still surface SOMETHING in the review step.
      const hasAny =
        Object.keys(routed.recipient).length > 0
        || Object.keys(routed.details).length > 0
        || Object.keys(routed.review).length > 0;
      const finalRouted = hasAny
        ? routed
        : { ...routed, review: { _form: messageFromError(err) } };
      setStepErrors(finalRouted);
      const target = earliestFailingStep(finalRouted);
      if (target) setCurrentStep(target);
      setSaveState("error");
    }
  }, [companyId, confirmed, saving, values, validateDetailsLocal]);

  // ============================================================
  // SUCCESS SURFACE — Step 8
  // Sibling render branch; the workflow form is NOT rendered.
  // "Send another" routes through cancel() — same reset codepath.
  // ============================================================
  if (saveState === "success") {
    return (
      <div
        className="feature-company-invitation__success"
        data-feature="company-invitation-success"
      >
        <EmptyState
          icon="fa-solid fa-link"
          title="Запрошення створено"
          description={
            submittedEmail
              ? `Запрошення для ${submittedEmail} створено. Користувач отримає in-app сповіщення, якщо акаунт існує. Email-розсилка вимкнена — скопіюйте посилання нижче.`
              : "Запрошення створено. Email-розсилка вимкнена — скопіюйте посилання нижче."
          }
          size="md"
        />
        {createdToken ? (
          <div className="feature-company-invitation__success-actions">
            <button
              type="button"
              className="ui-form__button ui-form__button--primary"
              disabled={Boolean(writesSuspended)}
              onClick={async () => {
                // R5-2: do not newly initiate clipboard/token feedback while
                // authority revalidation suspends writes; late continuations
                // must also skip applying feedback if suspension began.
                if (writesSuspendedRef.current) return;
                const link = `${window.location.origin}/companies/invitations/by-token/${createdToken}`;
                try {
                  if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(link);
                    if (writesSuspendedRef.current) return;
                    setCopyFeedback("Посилання скопійовано.");
                  } else {
                    if (writesSuspendedRef.current) return;
                    setCopyFeedback(`Скопіюйте вручну: ${link}`);
                  }
                } catch (_err) {
                  if (writesSuspendedRef.current) return;
                  setCopyFeedback(`Скопіюйте вручну: ${link}`);
                }
              }}
            >
              Копіювати посилання
            </button>
            {copyFeedback ? (
              <Text as="p" size="sm" tone="secondary">
                {copyFeedback}
              </Text>
            ) : null}
          </div>
        ) : null}
        <div className="feature-company-invitation__success-actions">
          <button
            type="button"
            className="ui-form__button ui-form__button--ghost"
            onClick={cancel}
          >
            Створити ще одне запрошення
          </button>
          {onCancelHref ? (
            <Link
              to={onCancelHref}
              className="feature-company-invitation__back-link"
            >
              Повернутися до компанії
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  // ============================================================
  // STEP RENDERERS — Step 9 (inline; co-located with their state)
  // ============================================================

  const renderRecipientStep = () => {
    const emailErr = stepErrors.recipient.email;
    return (
      <div
        className="feature-company-invitation__step"
        data-step="recipient"
      >
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="feature-company-invitation__step-heading"
        >
          Крок 1 — Отримувач
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Введіть електронну адресу людини, яку хочете запросити до
          компанії
          {companyName ? ` «${companyName}»` : ""}.
        </Text>

        <div className="ui-form__rows">
          <Field
            id="company-invitation-email"
            label="Email"
            help="На цю адресу буде привʼязано запрошення. Email-розсилка вимкнена — посилання можна скопіювати після створення."
            error={emailErr}
            required
          >
            <TextInput
              name="email"
              type="email"
              value={values.email}
              onChange={handleEmailChange}
              maxLength={EMAIL_MAX}
              autoComplete="email"
              placeholder="person@example.com"
              disabled={saving}
            />
          </Field>
        </div>
      </div>
    );
  };

  const renderDetailsStep = () => {
    const roleErr = stepErrors.details.role;
    const messageErr = stepErrors.details.message;
    return (
      <div
        className="feature-company-invitation__step"
        data-step="details"
      >
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="feature-company-invitation__step-heading"
        >
          Крок 2 — Роль і повідомлення
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Виберіть пропоновану роль і за бажанням додайте
          повідомлення.
        </Text>

        <div className="ui-form__rows">
          <Field
            id="company-invitation-role"
            label="Роль"
            help="Адмін може запрошувати інших та керувати компанією. Член — лише доступ до внутрішніх даних."
            error={roleErr}
            required
          >
            <Select
              name="role"
              value={values.role}
              onChange={handleRoleChange}
              options={roleOptions}
              disabled={saving}
            />
          </Field>

          <Field
            id="company-invitation-message"
            label="Повідомлення"
            help={`До ${MESSAGE_MAX} символів. Простий текст (необов'язково).`}
            error={messageErr}
          >
            <Textarea
              name="message"
              value={values.message}
              onChange={handleMessageChange}
              rows={4}
              maxLength={MESSAGE_MAX}
              placeholder="Наприклад: ми працюємо разом над комерційними об'єктами…"
              disabled={saving}
            />
          </Field>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const formError = stepErrors.review._form;
    const messagePreview = values.message.trim();
    return (
      <div
        className="feature-company-invitation__step"
        data-step="review"
      >
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="feature-company-invitation__step-heading"
        >
          Крок 3 — Перевірка
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Перевірте дані перед створенням. Email-розсилка вимкнена —
          після створення можна скопіювати посилання та очікувати in-app
          сповіщення.
        </Text>

        <dl className="feature-company-invitation__review">
          <div className="feature-company-invitation__review-row">
            <dt className="feature-company-invitation__review-label">
              Отримувач
            </dt>
            <dd className="feature-company-invitation__review-value">
              {values.email.trim()}
            </dd>
          </div>
          <div className="feature-company-invitation__review-row">
            <dt className="feature-company-invitation__review-label">
              Роль
            </dt>
            <dd className="feature-company-invitation__review-value">
              {roleLabelForLocal(values.role)}
            </dd>
          </div>
          <div className="feature-company-invitation__review-row">
            <dt className="feature-company-invitation__review-label">
              Повідомлення
            </dt>
            <dd className="feature-company-invitation__review-value feature-company-invitation__review-message">
              {messagePreview.length > 0 ? messagePreview : "—"}
            </dd>
          </div>
        </dl>

        <div className="feature-company-invitation__confirm">
          <Checkbox
            id="company-invitation-confirmed"
            name="confirmed"
            label="Підтверджую створення запрошення для цієї адреси."
            checked={confirmed}
            onChange={handleConfirmedChange}
            disabled={saving}
          />
        </div>

        <div className="ui-form__messages">
          {formError ? (
            <InlineError className="ui-form__message">{formError}</InlineError>
          ) : null}
        </div>
      </div>
    );
  };

  // ============================================================
  // PROGRESS INDICATOR + ACTION ROW
  // ============================================================

  const stepIndex =
    currentStep === "recipient" ? 0 : currentStep === "details" ? 1 : 2;

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (currentStep === "review") {
      submit();
    } else {
      goNext();
    }
  };

  const submitDisabled =
    currentStep === "review" ? !confirmed || saving : saving;
  const primaryLabel =
    currentStep === "review" ? "Надіслати запрошення" : "Далі";
  const showPrevious = currentStep !== "recipient";

  return (
    <form
      className="ui-form"
      data-feature="company-invitation-workflow"
      onSubmit={handleFormSubmit}
      noValidate
    >
      <ol
        className="feature-company-invitation__progress"
        aria-hidden="true"
      >
        <li
          className={
            "feature-company-invitation__progress-step"
            + (stepIndex >= 0 ? " is-active" : "")
            + (stepIndex > 0 ? " is-complete" : "")
          }
        >
          <span className="feature-company-invitation__progress-dot">1</span>
          <span className="feature-company-invitation__progress-label">
            Отримувач
          </span>
        </li>
        <li
          className={
            "feature-company-invitation__progress-step"
            + (stepIndex >= 1 ? " is-active" : "")
            + (stepIndex > 1 ? " is-complete" : "")
          }
        >
          <span className="feature-company-invitation__progress-dot">2</span>
          <span className="feature-company-invitation__progress-label">
            Деталі
          </span>
        </li>
        <li
          className={
            "feature-company-invitation__progress-step"
            + (stepIndex >= 2 ? " is-active" : "")
          }
        >
          <span className="feature-company-invitation__progress-dot">3</span>
          <span className="feature-company-invitation__progress-label">
            Перевірка
          </span>
        </li>
      </ol>

      {currentStep === "recipient" ? renderRecipientStep() : null}
      {currentStep === "details" ? renderDetailsStep() : null}
      {currentStep === "review" ? renderReviewStep() : null}

      <div className="ui-form__actions">
        {showPrevious ? (
          <button
            type="button"
            className="ui-form__button ui-form__button--ghost"
            onClick={goPrevious}
            disabled={saving}
          >
            Назад
          </button>
        ) : onCancelHref ? (
          <Link
            to={onCancelHref}
            className="ui-form__button ui-form__button--ghost feature-company-invitation__cancel-link"
          >
            Скасувати
          </Link>
        ) : (
          <button
            type="button"
            className="ui-form__button ui-form__button--ghost"
            onClick={cancel}
            disabled={saving}
          >
            Скасувати
          </button>
        )}
        <button
          type="submit"
          className="ui-form__button ui-form__button--primary"
          disabled={submitDisabled}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? (
            <Spinner size="sm" label="Надсилання" />
          ) : (
            primaryLabel
          )}
        </button>
      </div>
    </form>
  );
}

export default CompanyInvitationWorkflow;
